import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task, Milestone, Dependency, MilestoneStatus, DependencyType, Project } from '../types';
import { getProjectTasks } from '../services/taskService';
import { getProject } from '../services/projectService';
import { getProjectMilestones } from '../services/milestoneService';
import { getProjectDependencies } from '../services/dependencyService';
import TaskDetailDrawer from '../task/TaskDetailDrawer';
import { useTaskPolling, mergeTaskDeltas } from '../hooks/useTaskPolling';

const CARD_COLORS: Record<number, string> = {
  1:'#DC2626',2:'#D81B60',3:'#8E24AA',4:'#3949AB',5:'#1E88E5',6:'#039BE5',
  7:'#00ACC1',8:'#00897B',9:'#43A047',10:'#7CB342',11:'#F59E0B',12:'#FB8C00',
  13:'#F4511E',14:'#795548',15:'#546E7A',16:'#757575',
};
const DEFAULT_BAR = '#0E7490';
const DAY_MS = 86400000;

type ZoomLevel = 'day' | 'week' | 'month';
// Base density per day at each zoom level. These are minimums — the layout
// always stretches to fill the available timeline width when totalDays at
// base density would otherwise leave whitespace on the right.
const ZOOM_PX_PER_DAY: Record<ZoomLevel, number> = {
  day:   28,
  week:  14,
  month:  8,
};

const ROW_H        = 32;   // task / milestone row height
const HEADER_H     = 30;
const LEFT_PANEL_W_DEFAULT = 240;
const LEFT_PANEL_W_MIN     = 150;
const LEFT_PANEL_STORAGE   = 'dagrun_pm_gantt_title_width';
const BAR_TOP      = 4;
const BAR_H        = 20;
const GHOST_TOP    = 26;   // sits 2px below the main bar in a 32px row
const GHOST_H      = 4;

interface Props { pca: IPublicClientApplication; projectId: string; refreshKey?: number; }

export default function GanttView({ pca, projectId, refreshKey }: Props) {
  const [tasks, setTasks]           = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [dependencies, setDependencies] = useState<Dependency[]>([]);
  const [project, setProject]       = useState<Project | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [selected, setSelected]     = useState<Task | null>(null);
  const [zoom, setZoom]             = useState<ZoomLevel>('day');
  const [showBaseline, setShowBaseline] = useState(true);
  // Resizable title column width — persist to localStorage so user's choice
  // survives page reloads. Clamp to [MIN, 50% of container] when rendering.
  const [leftPanelW, setLeftPanelW] = useState<number>(() => {
    try {
      const raw = window.localStorage.getItem(LEFT_PANEL_STORAGE);
      const n = raw != null ? Number(raw) : NaN;
      if (Number.isFinite(n) && n >= LEFT_PANEL_W_MIN) return n;
    } catch { /* ignore */ }
    return LEFT_PANEL_W_DEFAULT;
  });
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [containerW, setContainerW] = useState<number>(0);

  // Callback ref — measures the outer container the moment it's
  // attached to the DOM. The previous useLayoutEffect/[] deps pattern
  // failed because the FIRST mount happens during loading=true, when
  // the outer div is hidden behind the spinner early-return. By the
  // time loading finishes the layout-effect has already fired
  // (against a null ref) and never runs again. A callback ref fires
  // EVERY time the element attaches, including after loading
  // completes. Also seats the ResizeObserver on the same attach.
  const roRef = useRef<ResizeObserver | null>(null);
  const setOuterRef = useCallback((el: HTMLDivElement | null) => {
    outerRef.current = el;
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!el) return;
    const initial = el.getBoundingClientRect().width;
    if (initial > 0) setContainerW(initial);
    if (typeof ResizeObserver !== 'undefined') {
      roRef.current = new ResizeObserver(entries => {
        for (const entry of entries) {
          const w = entry.contentRect.width;
          if (w > 0) setContainerW(w);
        }
      });
      roRef.current.observe(el);
    }
  }, []);
  // Disconnect on unmount.
  useEffect(() => () => { if (roRef.current) roRef.current.disconnect(); }, []);

  // When containerW is 0 (outer not yet measured), let leftPanelW
  // pass through unclamped. Without this fallback, maxLeftW collapses
  // to LEFT_PANEL_W_MIN (150) on the very first render and pins
  // effectiveLeftW to 150 — making the resize handle visibly drag
  // but with no observable layout change.
  const maxLeftW = containerW > 0
    ? Math.max(LEFT_PANEL_W_MIN, Math.floor(containerW * 0.5))
    : Math.max(LEFT_PANEL_W_MIN, leftPanelW);
  const effectiveLeftW = Math.min(Math.max(leftPanelW, LEFT_PANEL_W_MIN), maxLeftW);

  // Stable refs for the drag handlers — they read these refs instead
  // of capturing leftPanelW/effectiveLeftW from a closure, so the
  // handlers (registered ONCE on mount with empty deps) are immune
  // to stale state. Run 5/6 used closures inside beginResize and that
  // pattern still re-captured fresh state per click; this run uses
  // refs purely so there's no question about what the handler sees.
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWRef = useRef(0);
  const latestWRef = useRef(leftPanelW);
  useEffect(() => { latestWRef.current = leftPanelW; }, [leftPanelW]);

  // ONE set of window listeners. Mounted once. The draggingRef gates
  // them so they're cheap when not dragging.
  useEffect(() => {
    const onMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      const dx = ev.clientX - startXRef.current;
      const outerW = outerRef.current?.getBoundingClientRect().width ?? 0;
      const cap = outerW > 0
        ? Math.max(LEFT_PANEL_W_MIN, Math.floor(outerW * 0.5))
        : 9999;
      const next = Math.max(LEFT_PANEL_W_MIN, Math.min(startWRef.current + dx, cap));
      setLeftPanelW(next);
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try { window.localStorage.setItem(LEFT_PANEL_STORAGE, String(latestWRef.current)); } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('pointermove', onMove as EventListener);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('pointermove', onMove as EventListener);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const beginResize = (e: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    startXRef.current = e.clientX;
    startWRef.current = effectiveLeftW;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const [t, m, d, p] = await Promise.all([
        getProjectTasks(pca, projectId),
        getProjectMilestones(pca, projectId).catch(() => [] as Milestone[]),
        getProjectDependencies(pca, projectId).catch(() => [] as Dependency[]),
        getProject(pca, projectId).catch(() => null as Project | null),
      ]);
      setTasks(t.filter(tk => tk.dagrun_pm_startdate || tk.dagrun_pm_duedate));
      setMilestones(m.filter(ms => ms.dagrun_pm_duedate));
      setDependencies(d.filter(dep => dep.dagrun_pm_dependencytype === DependencyType.FS));
      setProject(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [pca, projectId]);

  useEffect(() => { reload(); }, [reload, refreshKey]);

  // Real-time refresh — tasks only (milestones/dependencies stay on the
  // manual reload path). Deferred while the drawer is open or the title
  // column is being resized. Merged rows pass the same date filter reload
  // applies, so undated tasks never leak into the timeline.
  useTaskPolling({
    pca, projectId,
    isPaused: () => loading || selected !== null || draggingRef.current,
    onTasks: deltas => setTasks(prev =>
      mergeTaskDeltas(prev, deltas).filter(tk => tk.dagrun_pm_startdate || tk.dagrun_pm_duedate)),
  });

  // dayPx starts from the zoom preset, then is scaled UP if the timeline
  // would otherwise leave empty space on the right. This fills the available
  // viewport width while still honouring zoom level as a minimum density.
  const { minDate, totalDays, dayPx, timelineWidth } = useMemo(() => {
    const today = Date.now();
    let min = Infinity;
    let max = -Infinity;
    for (const t of tasks) {
      const s = t.dagrun_pm_startdate ? new Date(t.dagrun_pm_startdate).getTime() : today;
      const e = t.dagrun_pm_duedate   ? new Date(t.dagrun_pm_duedate).getTime()   : s;
      if (s < min) min = s; if (e > max) max = e;
      if (t.dagrun_pm_baselinestart) {
        const bs = new Date(t.dagrun_pm_baselinestart).getTime();
        if (bs < min) min = bs;
      }
      if (t.dagrun_pm_baselineend) {
        const be = new Date(t.dagrun_pm_baselineend).getTime();
        if (be > max) max = be;
      }
    }
    for (const m of milestones) {
      if (m.dagrun_pm_duedate) {
        const d = new Date(m.dagrun_pm_duedate).getTime();
        if (d < min) min = d; if (d > max) max = d;
      }
    }
    if (!isFinite(min)) min = today - 7 * DAY_MS;
    if (!isFinite(max)) max = today + 14 * DAY_MS;
    min -= 3 * DAY_MS;
    max += 14 * DAY_MS;
    const total = Math.max(1, Math.ceil((max - min) / DAY_MS));
    const basePx = ZOOM_PX_PER_DAY[zoom];
    const availableTimelineW = Math.max(0, (containerW || 0) - effectiveLeftW - 1);
    const minWidth = total * basePx;
    // Fit-to-width: stretch dayPx so the timeline always fills the visible
    // area whenever the base density would leave whitespace on the right.
    // When the timeline overflows (zoomed in past content), we honour the
    // base density and let the inner scroll container handle overflow.
    const finalDayPx = (availableTimelineW > 0 && availableTimelineW > minWidth)
      ? availableTimelineW / total
      : basePx;
    return {
      minDate: min,
      totalDays: total,
      dayPx: finalDayPx,
      timelineWidth: Math.max(availableTimelineW, total * finalDayPx),
    };
  }, [tasks, milestones, zoom, containerW, effectiveLeftW]);

  if (loading) return <div style={{ padding: 24, color: 'var(--ink-3)' }}>Loading Gantt…</div>;
  if (error)   return <div style={{ padding: 24, color: 'var(--danger)' }}>{error}</div>;
  if (tasks.length === 0 && milestones.length === 0) {
    return <div style={{ padding: 24, color: 'var(--ink-4)' }}>No tasks or milestones with dates to display.</div>;
  }

  const xFor = (iso: string) => ((new Date(iso).getTime() - minDate) / DAY_MS) * dayPx;

  const todayX = ((Date.now() - minDate) / DAY_MS) * dayPx;
  const rowsTotal = milestones.length + tasks.length;
  const taskIdxById = new Map(tasks.map((t, i) => [t.dagrun_pm_taskid, i]));
  const todayVisible = todayX >= 0 && todayX <= timelineWidth;

  // Task bar geometry — used by both the row render and the dependency arrow overlay.
  const barFor = (t: Task) => {
    const sMs = t.dagrun_pm_startdate ? new Date(t.dagrun_pm_startdate).getTime() : minDate;
    const eMs = t.dagrun_pm_duedate   ? new Date(t.dagrun_pm_duedate).getTime()   : sMs + DAY_MS;
    const left  = Math.max(0, ((sMs - minDate) / DAY_MS) * dayPx);
    const right = Math.max(left + dayPx, ((eMs - minDate) / DAY_MS) * dayPx);
    return { left, right, width: right - left };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Gantt toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 14px',
        borderBottom: '0.5px solid var(--border-light)',
        background: 'var(--surface)',
      }}>
        <span style={{ fontSize: 11, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Zoom</span>
        <div style={{
          display: 'inline-flex', gap: 2, padding: 2,
          background: 'var(--surface-2)', borderRadius: 'var(--r-sm)',
        }}>
          {(['day','week','month'] as ZoomLevel[]).map(z => (
            <button
              key={z}
              onClick={() => setZoom(z)}
              style={{
                border: 'none',
                background: zoom === z ? 'var(--brand-mist)' : 'transparent',
                color:      zoom === z ? 'var(--brand)'     : 'var(--ink-3)',
                fontWeight: zoom === z ? 500 : 400,
                fontFamily: 'var(--font-ui)',
                fontSize: 12, padding: '4px 10px',
                borderRadius: 4, cursor: 'pointer', textTransform: 'capitalize',
                transition: 'background 0.12s, color 0.12s',
              }}
            >{z}</button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        {project?.dagrun_pm_baseline_capturedon && (
          <label style={{ fontSize: 12, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showBaseline}
              onChange={e => setShowBaseline(e.target.checked)}
              style={{ width: 14, height: 14, cursor: 'pointer' }}
            />
            Show baseline
          </label>
        )}
      </div>

      {/* Baseline legend — only rendered when a baseline has been captured */}
      {project?.dagrun_pm_baseline_capturedon && (
        <div style={{
          padding: '6px 14px',
          fontSize: 11, color: 'var(--ink-3)',
          background: 'var(--surface-2)',
          borderBottom: '0.5px solid var(--border-light)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            display: 'inline-block', width: 20, height: 4,
            background: 'var(--ink-5)', opacity: 0.6, borderRadius: 2,
          }} />
          <span>
            Baseline: <strong style={{ color: 'var(--ink-2)' }}>{project.dagrun_pm_baseline_label ?? '(unlabeled)'}</strong>
            {' '}captured {new Date(project.dagrun_pm_baseline_capturedon).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      )}

      {/* Gantt grid */}
      <div ref={setOuterRef} style={{ flex: 1, overflow: 'auto', background: 'var(--surface)', position: 'relative' }}>
        {/* Switched from flex to grid so the column split is driven by an
            explicit, React-controlled grid-template-columns string. The
            previous flex layout depended on each panel's flex-shrink/grow
            values; the resize handle sat inside the left panel which
            placed it inside the LEFT panel's stacking context — and the
            right panel (a later sibling with position:relative) painted
            on top of any handle overhang regardless of the handle's
            z-index. Lifting the handle to a sibling of the grid puts it
            in the OUTER container's stacking context where its z-index
            actually means something. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `${effectiveLeftW}px 1fr`,
        }}>
          {/* Left: task + milestone list. */}
          <div style={{ minWidth: 0, borderRight: '0.5px solid var(--border-light)', position: 'relative' }}>
            <div style={{
              padding: '0 10px',
              fontWeight: 600, fontSize: 12, color: 'var(--ink-2)',
              borderBottom: '0.5px solid var(--border-light)',
              background: 'var(--surface-2)',
              height: HEADER_H, display: 'flex', alignItems: 'center',
            }}>
              Task
            </div>
            {milestones.map(m => (
              <div
                key={m.dagrun_pm_milestoneid}
                style={{
                  padding: '0 10px', height: ROW_H,
                  borderBottom: '0.5px solid var(--border-light)',
                  fontSize: 13, color: 'var(--ink)',
                  display: 'flex', alignItems: 'center', gap: 6,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  background: 'var(--surface-2)',
                }}
              >
                <span style={{ color: 'var(--brand)' }}>◆</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.dagrun_pm_name}</span>
              </div>
            ))}
            {tasks.map(t => (
              <div
                key={t.dagrun_pm_taskid}
                onClick={() => setSelected(t)}
                style={{
                  padding: '0 10px', height: ROW_H,
                  borderBottom: '0.5px solid var(--border-light)',
                  cursor: 'pointer', fontSize: 13, color: 'var(--ink)',
                  display: 'flex', alignItems: 'center',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {t.dagrun_pm_name}
              </div>
            ))}
          </div>

          {/* Right: timeline — horizontal scroll inside; vertical scrolls with the outer container so left + right stay in sync */}
          <div style={{ flex: 1, position: 'relative', overflowX: 'auto', overflowY: 'visible' }}>
            {/* Timeline header */}
            <TimelineHeader
              zoom={zoom}
              minDate={minDate}
              totalDays={totalDays}
              dayPx={dayPx}
              width={timelineWidth}
              todayX={todayVisible ? todayX : null}
            />

            {/* Rows container */}
            <div style={{ position: 'relative', width: timelineWidth, height: rowsTotal * ROW_H }}>
              {/* Milestone rows */}
              {milestones.map((m, i) => {
                const top = i * ROW_H;
                return (
                  <div
                    key={m.dagrun_pm_milestoneid}
                    style={{
                      position: 'absolute',
                      left: 0, right: 0, top, height: ROW_H,
                      borderBottom: '0.5px solid var(--border-light)',
                      background: 'var(--surface-2)',
                    }}
                  />
                );
              })}
              {/* Task rows */}
              {tasks.map((t, i) => {
                const top = (milestones.length + i) * ROW_H;
                return (
                  <div
                    key={t.dagrun_pm_taskid}
                    style={{
                      position: 'absolute',
                      left: 0, right: 0, top, height: ROW_H,
                      borderBottom: '0.5px solid var(--border-light)',
                    }}
                  />
                );
              })}

              {/* Baseline ghost bars */}
              {showBaseline && tasks.map((t, i) => {
                if (!t.dagrun_pm_baselinestart && !t.dagrun_pm_baselineend) return null;
                const sIso = t.dagrun_pm_baselinestart ?? t.dagrun_pm_startdate;
                const eIso = t.dagrun_pm_baselineend   ?? t.dagrun_pm_duedate;
                if (!sIso || !eIso) return null;
                const bLeft  = xFor(sIso);
                const bRight = xFor(eIso);
                const bWidth = Math.max(dayPx / 2, bRight - bLeft);

                // Show only when there's a meaningful difference from the current schedule.
                const curS = t.dagrun_pm_startdate ? new Date(t.dagrun_pm_startdate).getTime() : 0;
                const curE = t.dagrun_pm_duedate   ? new Date(t.dagrun_pm_duedate).getTime()   : 0;
                const baseS = new Date(sIso).getTime();
                const baseE = new Date(eIso).getTime();
                const diff = Math.max(Math.abs(curS - baseS), Math.abs(curE - baseE));
                if (diff <= DAY_MS) return null;

                const top = (milestones.length + i) * ROW_H + GHOST_TOP;
                return (
                  <div
                    key={`ghost-${t.dagrun_pm_taskid}`}
                    title={`Baseline: ${new Date(sIso).toLocaleDateString()} – ${new Date(eIso).toLocaleDateString()}`}
                    style={{
                      position: 'absolute',
                      left: bLeft, top, width: bWidth, height: GHOST_H,
                      background: 'var(--ink-5)', opacity: 0.6,
                      borderRadius: 2,
                    }}
                  />
                );
              })}

              {/* Task bars */}
              {tasks.map((t, i) => {
                const { left, width } = barFor(t);
                const top = (milestones.length + i) * ROW_H + BAR_TOP;
                const color = t.dagrun_pm_cardcolor && CARD_COLORS[t.dagrun_pm_cardcolor]
                  ? CARD_COLORS[t.dagrun_pm_cardcolor]
                  : DEFAULT_BAR;
                return (
                  <div
                    key={t.dagrun_pm_taskid}
                    onClick={() => setSelected(t)}
                    title={t.dagrun_pm_name}
                    style={{
                      position: 'absolute',
                      left, top, width, height: BAR_H,
                      background: color,
                      border: t.dagrun_pm_isoncriticalpath ? '2px solid var(--danger)' : 'none',
                      borderRadius: 'var(--r-sm)',
                      cursor: 'pointer',
                      boxShadow: t.dagrun_pm_dependencyslipflag ? 'inset 0 0 0 2px var(--warning)' : 'none',
                    }}
                  />
                );
              })}

              {/* Milestone diamonds */}
              {milestones.map((m, i) => {
                if (!m.dagrun_pm_duedate) return null;
                const cx = xFor(m.dagrun_pm_duedate);
                const cy = i * ROW_H + ROW_H / 2;
                const complete = m.dagrun_pm_status === MilestoneStatus.Complete;
                return (
                  <svg
                    key={m.dagrun_pm_milestoneid}
                    width="14" height="14"
                    style={{ position: 'absolute', left: cx - 7, top: cy - 7, overflow: 'visible' }}
                  >
                    <title>{`${m.dagrun_pm_name} — ${new Date(m.dagrun_pm_duedate).toLocaleDateString()}`}</title>
                    <rect
                      x={2} y={2} width={10} height={10}
                      transform="rotate(45 7 7)"
                      fill={complete ? 'var(--brand)' : 'var(--surface)'}
                      stroke="var(--brand)"
                      strokeWidth={1.5}
                    />
                  </svg>
                );
              })}

              {/* Dependency arrows overlay */}
              <svg
                style={{
                  position: 'absolute',
                  left: 0, top: 0,
                  width: timelineWidth, height: rowsTotal * ROW_H,
                  pointerEvents: 'none',
                  overflow: 'visible',
                }}
              >
                <defs>
                  <marker id="gantt-arrow"   viewBox="0 0 10 10" refX="8" refY="5"
                          markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 Z" fill="var(--ink-4)" />
                  </marker>
                  <marker id="gantt-arrow-slip" viewBox="0 0 10 10" refX="8" refY="5"
                          markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 Z" fill="var(--brand)" />
                  </marker>
                </defs>
                {dependencies.map(dep => {
                  const pi = taskIdxById.get(dep._dagrun_pm_predecessortaskid_value);
                  const si = taskIdxById.get(dep._dagrun_pm_successortaskid_value);
                  if (pi == null || si == null) return null;
                  const pred = tasks[pi], succ = tasks[si];
                  const pb = barFor(pred), sb = barFor(succ);

                  // Offset from HEADER_H in overlay-from-rows space: overlay sits inside rows container
                  // (we already subtract HEADER_H since overlay starts at top:0 of rows container).
                  const py = (milestones.length + pi) * ROW_H + BAR_TOP + BAR_H / 2;
                  const sy = (milestones.length + si) * ROW_H + BAR_TOP + BAR_H / 2;

                  const startX = pb.right;
                  const endX   = sb.left;
                  const slip   = endX < startX; // successor starts before predecessor ends
                  const gap    = 8;

                  // L-shape: right gap → vertical → left gap to start
                  const midX = slip ? Math.max(startX + gap, endX - gap) : startX + gap;
                  const d = `M${startX},${py} L${midX},${py} L${midX},${sy} L${endX},${sy}`;

                  return (
                    <path
                      key={dep.dagrun_pm_dependencyid}
                      d={d}
                      fill="none"
                      stroke={slip ? 'var(--brand)' : 'var(--ink-4)'}
                      strokeWidth={1.5}
                      markerEnd={slip ? 'url(#gantt-arrow-slip)' : 'url(#gantt-arrow)'}
                    />
                  );
                })}
              </svg>

              {/* Today line */}
              {todayVisible && (
                <div
                  style={{
                    position: 'absolute',
                    left: todayX - 0.75, top: 0,
                    width: 1.5, height: rowsTotal * ROW_H,
                    background: 'var(--brand)',
                    opacity: 0.7,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Resize handle — TOP-LEVEL sibling of the grid container, NOT
            a child of either panel. Lives in the outer container's
            stacking context so its z-index lifts it above ANY content
            painted by the panels (which are sibling stacking contexts
            inside the grid and would otherwise paint over a handle
            placed inside the left panel). 10px hit-target centred on
            the column boundary. position:absolute relative to the
            outer container (which has position:relative) so it stays
            at left:effectiveLeftW-5 regardless of the inner grid's
            layout, and top:0/bottom:0 makes it span the full outer
            height. pointer-events:auto is explicit so no inherited
            "pointer-events:none" can suppress it. */}
        <div
          onPointerDown={beginResize}
          onMouseDown={beginResize}
          title="Drag to resize title column"
          aria-label="Resize Gantt title column"
          style={{
            position: 'absolute',
            left: effectiveLeftW - 5,
            top: 0, bottom: 0,
            width: 10,
            cursor: 'col-resize',
            zIndex: 100,
            background: 'transparent',
            touchAction: 'none',
            userSelect: 'none',
            pointerEvents: 'auto',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(14,116,144,0.18)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
          }}
        />
      </div>

      <TaskDetailDrawer task={selected} pca={pca} onClose={() => setSelected(null)} onSaved={reload} />
    </div>
  );
}

// Minimum horizontal distance between rendered labels at the timeline
// header. Anything closer gets deduped (kept as a tick line but label
// suppressed) so the header can never show overlapping text.
const LABEL_MIN_GAP_PX = 48;

function TimelineHeader({
  zoom, minDate, totalDays, dayPx, width, todayX,
}: { zoom: ZoomLevel; minDate: number; totalDays: number; dayPx: number; width: number; todayX: number | null }) {
  // Build candidate tick marks + labels per zoom level. Label suppression
  // happens in the render pass below (based on dayPx × spacing) so this
  // builder can stay simple.
  const ticks: { x: number; label: string; strong?: boolean }[] = [];

  if (zoom === 'day') {
    // At a dense zoom, every day gets a label; at a sparse zoom (dayPx
    // small) we thin out to every Nth day based on LABEL_MIN_GAP_PX.
    const step = Math.max(1, Math.ceil(LABEL_MIN_GAP_PX / Math.max(dayPx, 1)));
    for (let i = 0; i < totalDays; i += step) {
      const d = new Date(minDate + i * DAY_MS);
      ticks.push({
        x: i * dayPx,
        label: `${d.toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 3)} ${d.getDate()}`,
        strong: d.getDay() === 1,
      });
    }
  } else if (zoom === 'week') {
    // Tick every week (Monday). Skip labels that would collide at very
    // narrow zoom.
    const pxPerWeek = dayPx * 7;
    const weekStep = Math.max(1, Math.ceil(LABEL_MIN_GAP_PX / Math.max(pxPerWeek, 1)));
    // Align to Monday of minDate's week.
    const start = new Date(minDate);
    const dow = start.getDay();
    const offsetToMonday = ((dow + 6) % 7);    // 0 if Monday
    const firstMondayMs = start.getTime() - offsetToMonday * DAY_MS;
    let count = 0;
    for (let ms = firstMondayMs; ms <= minDate + totalDays * DAY_MS; ms += 7 * DAY_MS) {
      if (count % weekStep === 0) {
        const d = new Date(ms);
        ticks.push({
          x: ((ms - minDate) / DAY_MS) * dayPx,
          label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          strong: true,
        });
      }
      count++;
    }
  } else {
    // month: tick + label at the 1st of each month; thin out if months
    // are too close together.
    const start = new Date(minDate);
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    if (cursor.getTime() < minDate) {
      cursor = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    }
    while (cursor.getTime() <= minDate + totalDays * DAY_MS) {
      const daysFromMin = (cursor.getTime() - minDate) / DAY_MS;
      ticks.push({
        x: daysFromMin * dayPx,
        label: cursor.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        strong: true,
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
  }

  // Final render-side dedup: if two labels would render within
  // LABEL_MIN_GAP_PX of each other, drop the second.
  const renderedTicks: { x: number; label: string; strong?: boolean }[] = [];
  let lastLabelX = -Infinity;
  ticks.forEach(t => {
    if (t.x - lastLabelX >= LABEL_MIN_GAP_PX) {
      renderedTicks.push(t);
      lastLabelX = t.x;
    }
  });

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 5,
      width, height: HEADER_H,
      background: 'var(--surface-2)',
      borderBottom: '0.5px solid var(--border-light)',
    }}>
      {renderedTicks.map((t, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: t.x, top: 0, height: '100%',
            borderLeft: t.strong
              ? '0.5px solid var(--border)'
              : '0.5px solid var(--border-light)',
            paddingLeft: 4, paddingRight: 4,
            fontSize: 11, color: 'var(--ink-4)',
            fontFamily: 'var(--font-mono)',
            display: 'flex', alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {t.label}
        </div>
      ))}
      {todayX != null && (
        <div
          style={{
            position: 'absolute',
            left: todayX, top: 2,
            transform: 'translateX(-50%)',
            fontSize: 10, fontWeight: 600,
            color: 'var(--brand)',
            background: 'var(--surface)',
            padding: '0 4px',
            borderRadius: 'var(--r-sm)',
            pointerEvents: 'none',
          }}
        >
          Today
        </div>
      )}
    </div>
  );
}
